/**
 * Campaign-slug attribution — the shared contract between every marketing
 * surface that MINTS a ?campaign= value (landing pages, nav CTAs) and the
 * public routine wizard that CONSUMES it.
 *
 * The slug flows verbatim into auth.users.raw_user_meta_data on the wizard's
 * anonymous signup, and that metadata rides in the auth cookies — an
 * unbounded value (8-15KB blob) balloons the cookies past header limits and
 * durably breaks the account. Accept only short slug-shaped tokens; anything
 * else is dropped entirely. Import THIS validator on both sides — a stricter
 * copy on one side silently drops the other side's attribution.
 */
export const CAMPAIGN_SLUG_RE = /^[a-zA-Z0-9_-]{1,64}$/;

export function sanitizeCampaign(raw: string | null | undefined): string | null {
  if (typeof raw !== 'string' || !CAMPAIGN_SLUG_RE.test(raw)) return null;
  return raw;
}

/**
 * Channel-intent param — landing pages MINT `?channel=` by the promise they
 * make ("Tessa calls your team" → calls; async check-ins → async), and the
 * routine wizards CONSUME it to preselect the reach card. Closed enum:
 * anything else is dropped to null — never defaulted — so an absent/mangled
 * param falls back to the template's own channels, not a landing page's claim.
 */
export type ChannelIntent = 'calls' | 'async' | 'callin';

export function sanitizeChannelIntent(raw: string | null | undefined): ChannelIntent | null {
  return raw === 'calls' || raw === 'async' || raw === 'callin' ? raw : null;
}

/**
 * The effective action_channels for a chosen reach intent — ONE rule shared by
 * both wizards so the saved routine always matches the card the user picked.
 * 'calls' promotes phone to the front (the template's other channels stay as
 * the fallback ladder). 'async' is an explicit DEMOTION, not a no-op: phone
 * and sms are stripped so a phone-first template actually becomes async (and
 * the phone launch gate stays quiet), falling back to email if that empties
 * the list.
 */
export function channelsForIntent(
  intent: ChannelIntent,
  templateChannels: string[] | null | undefined,
): string[] {
  const channels = templateChannels || [];
  // Anonymous call-in: Tessa reaches NOBODY outbound — people call HER on the
  // shared anonymous line with the routine's code. Empty channels is what
  // keeps the dispatch/launch machinery structurally inert (routines.callin_only).
  if (intent === 'callin') return [];
  if (intent === 'calls') return ['phone', ...channels.filter((c) => c !== 'phone')];
  const demoted = channels.filter((c) => c !== 'phone' && c !== 'sms');
  return demoted.length > 0 ? demoted : ['email'];
}
